import { ApiError } from "@/lib/api-client";
import { formatWaitMinutes, readRetryAfterSeconds } from "@/lib/retry-after";

// Maps a thrown auth error to a Banner descriptor. We branch ONLY on the structured
// error code (never the raw message/details), and the copy never echoes PHI, a
// token, an OTP code, or which sub-step failed — the backend envelopes are
// deliberately byte-shaped to prevent enumeration.
//
// [Source: src/application/common/exceptions.py — AUTH-001/002/004/005/503;
//  src/application/authentication/mfa_verify/rate_limiter.py — 429 AUTH-019]

export interface AuthBanner {
  /**
   * `info` = a settled state that resolves itself (a rate-limit window rolling
   * forward); `warn`/`danger` = something to check or retry. `toAuthBanner`
   * itself never returns `info` — the variant is here for the per-slice mappers
   * that reuse this descriptor, the change-password limiter (AUTH-016) first,
   * and it matches the `info` posture the change-EMAIL limiter already takes.
   */
  variant: "info" | "warn" | "danger";
  message: string;
}

const GENERIC: AuthBanner = {
  variant: "warn",
  message: "Something went wrong. Please try again.",
};

const BY_CODE: Record<string, AuthBanner> = {
  // Login
  "AUTH-001": { variant: "danger", message: "Invalid email or password." },
  "AUTH-002": { variant: "warn", message: "Too many failed attempts. Please try again later." },
  // MFA verify / send / token
  "AUTH-004": {
    variant: "danger",
    message: "That code is invalid or has expired. Request a new code.",
  },
  // AUTH-005 caps how many codes go OUT; AUTH-019 caps how many come back in.
  // The second is not here — its copy names the wait the server sent, so it is
  // built by `mfaVerifyRateLimitedBanner` below.
  "AUTH-005": {
    variant: "warn",
    message: "Too many code requests. Please wait before trying again.",
  },
  // Any step
  "AUTH-503": {
    variant: "warn",
    message: "Sign-in is temporarily unavailable. Please try again in a moment.",
  },
};

/**
 * 429 AUTH-019 — the per-token mfa/verify limiter (DW-141). Not in `BY_CODE`
 * because the wait has to be read off `details.retry_after_s`, exactly as the
 * ORG-013 / AUTH-015 / AUTH-016 twins read it, and a static record cannot.
 *
 * `warn`, not `info`: the two other refusals this screen shows in the same
 * banner slot — AUTH-002's login lockout and AUTH-005's send throttle — are both
 * `warn`, and one screen giving three rate limits two different weights reads as
 * a severity signal that isn't there.
 *
 * What the copy must not do. It says the codes were ENTERED too often, never
 * whether any of them was right: `invalid_code` and `expired` already collapse
 * into one byte-identical 401 to stop exactly that inference, and a chatty 429
 * would hand back the distinction the 401 withholds. It also never says
 * "locked": nothing is disabled, the window rolls forward on its own, and a
 * clinician told their account is locked calls support instead of waiting.
 * Naming the wait is what makes waiting an instruction rather than a guess —
 * without it the only strategy left is to keep submitting, which is the very
 * behaviour the limiter exists to stop.
 *
 * Seconds up to 90s, whole minutes above — the `change-email-error.ts` rounding,
 * and the right one here: the window is 300s, so a real wait is routinely tens
 * of seconds and "in about 1 minute" for 45s is not actionable.
 */
function mfaVerifyRateLimitedBanner(details: unknown): AuthBanner {
  const seconds = readRetryAfterSeconds(details);
  if (seconds === null) {
    return { variant: "warn", message: "Too many codes entered. Please wait before trying again." };
  }
  const wait = seconds <= 90 ? `${Math.ceil(seconds)} seconds` : formatWaitMinutes(seconds);
  return {
    variant: "warn",
    message: `Too many codes entered. Please try again in about ${wait}.`,
  };
}

export function toAuthBanner(error: unknown): AuthBanner {
  // Before the record lookup: this is the one auth code whose copy depends on a
  // value the server sent, so it cannot be a constant entry in `BY_CODE`.
  if (error instanceof ApiError && error.code === "AUTH-019") {
    return mfaVerifyRateLimitedBanner(error.details);
  }
  // Own-property guard: `code` is a server-supplied string, so a value colliding
  // with an Object.prototype member (`toString`, `constructor`, `__proto__`) would
  // resolve through the prototype chain to a truthy non-Banner and slip past
  // `?? GENERIC`. The own-property check restricts the lookup to real entries.
  if (
    error instanceof ApiError &&
    Object.prototype.hasOwnProperty.call(BY_CODE, error.code)
  ) {
    return BY_CODE[error.code];
  }
  // Non-ApiError (network failure, AuthExpiredError, etc.) → generic, no internals.
  return GENERIC;
}
